import { useEffect, useMemo, useState } from "react";
import { Download, FileCode2 } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { ValidationBadge } from "@/components/organisms/studio/ArtifactList";
import { useEngineArtifactBody, useEngineArtifacts } from "@/hooks/migrations/engine";
import {
  artifactChains,
  type ArtifactChainEntry,
  type DisplayedArtifact,
} from "@/lib/migrations/apply";
import { asValidationOutcome } from "@/lib/studio/validation";
import { engineErrorMessage, engineTaskPath } from "@/services/migrations/engine";
import type { EngineArtifactRecord, EngineTaskReport } from "@/services/migrations/engine";

/**
 * The artifacts one engine task produced, their VERSION HISTORY, and the bytes of the
 * one currently on screen.
 *
 * WHY THIS NO LONGER REUSES `InlineArtifactPreview`. That component fetches through
 * `apiBlob` and calls `blob.text()`, which is right for the playbook's artifact route
 * because that route serves raw bytes. The engine's does not: `GET …/artifacts/
 * {component_key}` returns JSON — the same metadata the list carries, plus a `content`
 * string. Pointing the shared preview at it would render the JSON envelope verbatim in a
 * code block and call it the artifact. `resolveUrl` rewrites the URL, not the parsing, so
 * there was no way to make it read this route without changing a component the playbook
 * path also renders. This surface reads its own route instead, and the playbook preview
 * is left byte-for-byte as it was.
 *
 * WHAT THE VERSION LIST IS FOR. Artifacts are append-only: a re-compile with different
 * inputs makes version N+1 and supersedes N rather than overwriting it. Showing only the
 * current version would hide that a component was recompiled at all — and a recompile is
 * precisely the event that voids a recorded approval, because `compile_key` moves with
 * it. So the whole chain renders, superseded revisions stay reachable through
 * `?version=N`, and every row says which one it is.
 *
 * WHAT IT REPORTS UPWARD. `onDisplayedChange` tells the approval gate which artifact the
 * reader actually has open. That is not decoration: the gate refuses to offer a decision
 * unless the current artifact's body is on screen (see `lib/migrations/apply.ts`).
 */
export function EngineArtifactList({
  planId,
  task,
  onArtifactsChange,
  onDisplayedChange,
}: {
  planId: string;
  task: EngineTaskReport;
  /** Reports the loaded state + rows up to the approval gate. */
  onArtifactsChange?: (state: { loaded: boolean; artifacts: EngineArtifactRecord[] }) => void;
  /** Reports which artifact's BODY is open, or `null` when none is. */
  onDisplayedChange?: (displayed: DisplayedArtifact | null) => void;
}) {
  const taskPath = engineTaskPath(task);
  const artifactsQ = useEngineArtifacts(planId, taskPath);
  const records = useMemo(() => artifactsQ.data?.artifacts ?? [], [artifactsQ.data]);
  const chains = useMemo(() => artifactChains(records), [records]);

  // `null` covers both "nothing opened yet" and "opened and then closed". The gate
  // treats them identically, because both mean the bytes are not on screen now.
  const [openKey, setOpenKey] = useState<string | null>(null);

  const outcome = asValidationOutcome(task.validation?.outcome ?? task.validation_outcome);
  const verdict = outcome
    ? { outcome, errors: task.validation?.errors ?? [], reason: task.validation?.reason ?? null }
    : null;

  const loaded = artifactsQ.isSuccess;
  useEffect(() => {
    onArtifactsChange?.({ loaded, artifacts: records });
  }, [loaded, records, onArtifactsChange]);

  const open = useMemo(() => {
    if (!openKey) return null;
    return (
      chains.flatMap((chain) => chain.entries).find((entry) => rowKey(entry.record) === openKey) ??
      null
    );
  }, [chains, openKey]);

  useEffect(() => {
    onDisplayedChange?.(
      open
        ? {
            component_key: open.record.component_key,
            version: open.record.version,
            compile_key: open.record.compile_key,
            superseded: open.record.superseded,
          }
        : null
    );
  }, [open, onDisplayedChange]);

  if (artifactsQ.isLoading) {
    return <p className="font-mono text-xs text-muted-foreground">Loading artifacts…</p>;
  }
  if (artifactsQ.isError) {
    return (
      <p className="font-mono text-xs text-destructive">
        Artifacts could not be loaded: {engineErrorMessage(artifactsQ.error)}. The verdict above
        stands; nothing on this task can be approved until the artifact it names can be read.
      </p>
    );
  }
  if (records.length === 0) {
    return (
      <p className="font-mono text-xs text-muted-foreground">This task produced no artifact.</p>
    );
  }

  return (
    <div className="space-y-4">
      {chains.map((chain) => (
        <section key={chain.component_key} className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="truncate font-mono text-sm text-foreground">
              {chain.current?.filename || chain.component_key}
            </span>
            {/* Deployable: an engine artifact is metadata the org can check. A missing
                verdict therefore reads "not checked", never as silent approval. */}
            <ValidationBadge verdict={verdict} deployable />
          </div>

          {chain.entries.length > 1 ? (
            <p className="font-mono text-[11px] text-[hsl(var(--accent-warm))]">
              This component was compiled {chain.entries.length} times. Every recompile issues a new
              compile key, which voids any approval recorded against an earlier one.
            </p>
          ) : null}

          <ul className="divide-y divide-border">
            {chain.entries.map((entry) => (
              <VersionRow
                key={rowKey(entry.record)}
                entry={entry}
                taskCompileKey={task.compile_key}
                open={openKey === rowKey(entry.record)}
                onToggle={() =>
                  setOpenKey((current) =>
                    current === rowKey(entry.record) ? null : rowKey(entry.record)
                  )
                }
              />
            ))}
          </ul>

          {open && open.record.component_key === chain.component_key ? (
            <ArtifactBody planId={planId} taskPath={taskPath} entry={open} />
          ) : null}
        </section>
      ))}
    </div>
  );
}

function rowKey(record: EngineArtifactRecord): string {
  return `${record.component_key}@${record.version}`;
}

function VersionRow({
  entry,
  taskCompileKey,
  open,
  onToggle,
}: {
  entry: ArtifactChainEntry;
  taskCompileKey: string;
  open: boolean;
  onToggle: () => void;
}) {
  const { record } = entry;
  // The artifact's own key against the task's. They differ exactly when this row is a
  // revision the task has moved past — the same fact `superseded` carries, restated in
  // the currency the approval ledger and the confirmation token are denominated in.
  const keyMatchesTask = Boolean(taskCompileKey) && record.compile_key === taskCompileKey;

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
          <span className="text-foreground">v{record.version}</span>
          <span>{record.metadata_type}</span>
          <span>{formatBytes(record.size_bytes)}</span>
          <span>{shortSha(record.content_sha)}</span>
          {entry.current ? (
            <Badge variant="secondary">current</Badge>
          ) : (
            <span
              className="shrink-0 rounded border border-[hsl(var(--accent-warm))]/45 px-1 py-0.5 text-[10px] uppercase leading-none text-[hsl(var(--accent-warm))]"
              title={
                entry.superseded_by != null
                  ? `Superseded by version ${entry.superseded_by}. This is not the current artifact.`
                  : "Superseded. This is not the current artifact."
              }
            >
              superseded{entry.superseded_by != null ? ` by v${entry.superseded_by}` : ""}
            </span>
          )}
          {entry.current && !keyMatchesTask ? (
            <span className="text-destructive">compile key differs from the task&apos;s</span>
          ) : null}
        </div>
        <div className="mt-0.5 break-all font-mono text-[10px] text-muted-foreground/70">
          compile key {record.compile_key || "—"}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onToggle}>
        <FileCode2 className="mr-1 h-3.5 w-3.5" />
        {open ? "Hide" : "Read"} v{record.version}
      </Button>
    </li>
  );
}

/**
 * The bytes, at one explicit version.
 *
 * The version is always sent. A viewer that requested "the latest" would render whatever
 * the server last compiled while its own header claimed to be showing the revision the
 * reader picked — and this body is the thing an approval is a statement about.
 */
function ArtifactBody({
  planId,
  taskPath,
  entry,
}: {
  planId: string;
  taskPath: string;
  entry: ArtifactChainEntry;
}) {
  const { record } = entry;
  const bodyQ = useEngineArtifactBody(
    planId,
    taskPath,
    record.component_key_encoded,
    record.version
  );

  if (bodyQ.isLoading) {
    return <p className="font-mono text-xs text-muted-foreground">Loading v{record.version}…</p>;
  }
  if (bodyQ.isError || !bodyQ.data) {
    return (
      <p className="font-mono text-xs text-destructive">
        v{record.version} could not be read: {engineErrorMessage(bodyQ.error)}
      </p>
    );
  }

  const body = bodyQ.data;
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted/20">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="truncate font-mono text-[11px] text-muted-foreground">
          {body.filename} · v{body.version} · {body.content_type}
          {entry.current ? "" : " · superseded revision"}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => download(body.filename, body.content_type, body.content)}
        >
          <Download className="mr-1 h-3.5 w-3.5" />
          Download
        </Button>
      </div>
      <pre className="max-h-96 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-foreground">
        {body.content}
      </pre>
    </div>
  );
}

/**
 * Save the artifact under the name and MIME type the SERVER sent.
 *
 * Both come off the wire. A client-side extension map would be a second copy of a fact
 * this response already carries, and it drifts the first time the engine emits a family
 * whose artifact is not `.xml`.
 */
function download(filename: string, contentType: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: contentType || "text/plain" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "artifact";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function shortSha(sha: string): string {
  return sha ? sha.slice(0, 12) : "no sha";
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}
